<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { message, Modal, Form, Input, Select, InputNumber, Switch, Button, Table, Tag, Card } from 'ant-design-vue';
import {
    getConsultationLevels,
    getConsultationLevel,
    createConsultationLevel,
    updateConsultationLevel
} from '@/api/consultation/consultationApi';
import { usePermission } from '@/composables/usePermission';

const router = useRouter();
const { canView, canCreate, canUpdate } = usePermission('ConsultationLevels');

const goConsultantsByLevel = (code) => {
    router.push({ name: 'Consultants', query: { level: code } });
};

const loading = ref(false);
const levels = ref([]);
const editOpen = ref(false);
const editLoading = ref(false);
const isCreate = ref(false);
const current = ref(null);
const rulesDraft = ref([]);
const consultantCountHint = ref(0);

const modalTitle = computed(() => (isCreate.value ? '新增等級' : '編輯等級'));

const metricOptions = [
    { label: '淨資產', value: 'net_asset_value' },
    { label: '月收入', value: 'monthly_income' },
    { label: '總資產', value: 'total_assets' },
    { label: '總負債', value: 'total_liabilities' },
    { label: '月現金流', value: 'monthly_cash_flow' }
];

const operatorOptions = [
    { label: '大於等於', value: '>=' },
    { label: '小於等於', value: '<=' },
    { label: '等於', value: '=' },
    { label: '大於', value: '>' },
    { label: '小於', value: '<' }
];

const metricLabelMap = Object.fromEntries(metricOptions.map((o) => [o.value, o.label]));
const operatorLabelMap = Object.fromEntries(operatorOptions.map((o) => [o.value, o.label]));

const formatRule = (rule) => {
    const metric = rule.metric === 'net_assets' ? 'net_asset_value' : rule.metric;
    const label = metricLabelMap[metric] || metric;
    const op = operatorLabelMap[rule.operator] || rule.operator;
    const n = Number(rule.threshold);
    const threshold = Number.isFinite(n) ? n : rule.threshold;
    return `${label} ${op} ${threshold} 萬`;
};

const columns = [
    { title: '代碼', dataIndex: 'code', key: 'code', width: 88 },
    { title: '名稱', dataIndex: 'name', key: 'name', width: 120, ellipsis: true },
    { title: '排序', dataIndex: 'rank', key: 'rank', width: 72 },
    {
        title: '每月最少時段',
        dataIndex: 'min_monthly_open_slots',
        key: 'min_slots',
        width: 120
    },
    { title: '規則', key: 'rules', ellipsis: true },
    { title: '啟用', key: 'active', width: 80 },
    { title: '諮詢師數', dataIndex: 'active_consultants_count', key: 'cc', width: 96 },
    { title: '操作', key: 'actions', width: 88, fixed: 'right' }
];

const suggestNextCode = () => {
    const nums = levels.value
        .map((l) => /^L(\d+)$/i.exec(String(l.code || ''))?.[1])
        .filter(Boolean)
        .map(Number);
    const next = nums.length ? Math.max(...nums) + 1 : 1;
    return `L${next}`;
};

const suggestNextRank = () => {
    const ranks = levels.value.map((l) => Number(l.rank)).filter((n) => Number.isFinite(n));
    return ranks.length ? Math.max(...ranks) + 1 : 1;
};

const load = async () => {
    loading.value = true;
    try {
        const res = await getConsultationLevels();
        if (res.status !== 1) throw new Error(res.message || '載入失敗');
        levels.value = res.data || [];
    } catch (e) {
        message.error(e.message || '載入失敗');
    } finally {
        loading.value = false;
    }
};

const openCreate = () => {
    isCreate.value = true;
    consultantCountHint.value = 0;
    current.value = {
        code: suggestNextCode(),
        name: '',
        rank: suggestNextRank(),
        min_monthly_open_slots: 0,
        active: true
    };
    rulesDraft.value = [];
    editOpen.value = true;
};

const openEdit = async (id) => {
    isCreate.value = false;
    const row = levels.value.find((x) => x.id === id);
    consultantCountHint.value = row?.active_consultants_count ?? 0;
    editLoading.value = true;
    try {
        const res = await getConsultationLevel(id);
        if (res.status !== 1) throw new Error(res.message || '載入失敗');
        current.value = res.data;
        rulesDraft.value = (res.data.rules || []).map((r) => ({
            metric: r.metric === 'net_assets' ? 'net_asset_value' : r.metric,
            operator: r.operator,
            threshold: Number(r.threshold)
        }));
        editOpen.value = true;
    } catch (e) {
        message.error(e.message || '載入失敗');
    } finally {
        editLoading.value = false;
    }
};

const addRule = () => {
    rulesDraft.value.push({ metric: 'net_asset_value', operator: '>=', threshold: 0 });
};

const removeRule = (idx) => {
    rulesDraft.value.splice(idx, 1);
};

const save = async () => {
    if (!current.value) return;

    const code = String(current.value.code || '').trim();
    const name = String(current.value.name || '').trim();
    if (isCreate.value && !code) {
        message.warning('請填寫等級代碼');
        return;
    }
    if (!name) {
        message.warning('請填寫等級名稱');
        return;
    }

    editLoading.value = true;
    try {
        const payload = {
            name,
            rank: Number(current.value.rank) || 1,
            min_monthly_open_slots: Number(current.value.min_monthly_open_slots) || 0,
            active: current.value.active,
            rules: rulesDraft.value
        };

        const res = isCreate.value
            ? await createConsultationLevel({ ...payload, code })
            : await updateConsultationLevel(current.value.id, payload);

        if (res.status !== 1) throw new Error(res.message || '儲存失敗');
        message.success(isCreate.value ? '已新增' : '已儲存');
        editOpen.value = false;
        await load();
    } catch (e) {
        message.error(e.message || '儲存失敗');
    } finally {
        editLoading.value = false;
    }
};

onMounted(load);
</script>

<template>
    <div class="consultation-levels-page pb-10">
        <p v-if="!canView" class="text-slate-500">無檢視權限</p>

        <template v-else>
            <Card :bordered="false" class="admin-panel overflow-hidden">
                <div class="levels-toolbar">
                    <span class="levels-toolbar__title">等級列表</span>
                    <Button type="primary" :disabled="!canCreate" @click="openCreate">
                        新增等級
                    </Button>
                </div>

                <Table
                    class="admin-table-surface"
                    :columns="columns"
                    :data-source="levels"
                    :loading="loading"
                    row-key="id"
                    :pagination="false"
                    size="middle"
                    :scroll="{ x: 960 }"
                    :locale="{ emptyText: '尚無資料' }"
                >
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.key === 'min_slots'">
                            <span v-if="record.min_monthly_open_slots > 0" class="text-sm tabular-nums">
                                {{ record.min_monthly_open_slots }}
                            </span>
                            <span v-else class="text-slate-400 text-sm">—</span>
                        </template>
                        <template v-else-if="column.key === 'rules'">
                            <div v-if="record.rules?.length" class="flex flex-wrap gap-1.5 py-0.5">
                                <Tag
                                    v-for="(rule, idx) in record.rules"
                                    :key="rule.id ?? idx"
                                    class="m-0 max-w-full whitespace-normal text-left leading-snug"
                                >
                                    {{ formatRule(rule) }}
                                </Tag>
                            </div>
                            <span v-else class="text-slate-400 text-sm">—</span>
                        </template>
                        <template v-else-if="column.key === 'active'">
                            <Tag :color="record.active ? 'success' : 'default'">
                                {{ record.active ? '啟用' : '停用' }}
                            </Tag>
                        </template>
                        <template v-else-if="column.key === 'cc'">
                            <Button
                                v-if="record.active_consultants_count > 0"
                                type="link"
                                size="small"
                                class="!px-0 tabular-nums"
                                @click="goConsultantsByLevel(record.code)"
                            >
                                {{ record.active_consultants_count }}
                            </Button>
                            <span v-else class="text-slate-400 text-sm">0</span>
                        </template>
                        <template v-else-if="column.key === 'actions'">
                            <div class="admin-table-actions">
                                <Button
                                    size="small"
                                    class="admin-table-actions__btn"
                                    :disabled="!canUpdate"
                                    @click="openEdit(record.id)"
                                >
                                    編輯
                                </Button>
                            </div>
                        </template>
                    </template>
                </Table>
            </Card>
        </template>

        <Modal
            v-model:open="editOpen"
            :title="modalTitle"
            :width="640"
            wrap-class-name="edit-modal"
            :confirm-loading="editLoading"
            ok-text="儲存"
            cancel-text="取消"
            destroy-on-close
            @ok="save"
        >
            <div v-if="current" class="level-modal">
                <Form layout="vertical" class="level-modal__form">
                    <div class="level-modal__grid" :class="{ 'level-modal__grid--create': isCreate }">
                        <Form.Item
                            v-if="isCreate"
                            label="代碼"
                            class="!mb-0"
                            extra="建立後不可改"
                        >
                            <Input
                                v-model:value="current.code"
                                placeholder="例：L4"
                                allow-clear
                                :maxlength="8"
                            />
                        </Form.Item>
                        <Form.Item label="名稱" class="!mb-0">
                            <Input
                                v-model:value="current.name"
                                placeholder="例：S+ 級"
                                allow-clear
                            />
                        </Form.Item>
                        <Form.Item label="排序" class="!mb-0" extra="越小越前">
                            <InputNumber
                                v-model:value="current.rank"
                                class="w-full"
                                :min="1"
                            />
                        </Form.Item>
                        <Form.Item
                            label="每月最少時段"
                            class="!mb-0"
                            extra="0＝不強制"
                        >
                            <InputNumber
                                v-model:value="current.min_monthly_open_slots"
                                class="w-full"
                                :min="0"
                                :max="500"
                                addon-after="時段"
                            />
                        </Form.Item>
                    </div>

                    <div class="level-modal__active">
                        <div>
                            <div class="level-modal__active-label">啟用</div>
                            <div class="level-modal__active-hint">停用後不會參與自動配對</div>
                        </div>
                        <Switch
                            v-model:checked="current.active"
                            checked-children="開"
                            un-checked-children="關"
                        />
                    </div>
                </Form>

                <div class="level-modal__rules">
                    <div class="level-modal__rules-head">
                        <span class="level-modal__rules-title">規則條件</span>
                        <Button type="dashed" size="small" @click="addRule">
                            新增規則
                        </Button>
                    </div>

                    <p v-if="rulesDraft.length === 0" class="level-modal__rules-empty">
                        尚未設定（無規則不參與自動配對）
                    </p>

                    <div v-else class="level-modal__rules-list">
                        <div
                            v-for="(r, idx) in rulesDraft"
                            :key="idx"
                            class="level-modal__rule"
                        >
                            <Select
                                v-model:value="r.metric"
                                :options="metricOptions"
                                placeholder="指標"
                                class="level-modal__rule-metric"
                            />
                            <Select
                                v-model:value="r.operator"
                                :options="operatorOptions"
                                class="level-modal__rule-op"
                            />
                            <InputNumber
                                v-model:value="r.threshold"
                                class="level-modal__rule-threshold"
                                :min="0"
                                :step="1"
                                addon-after="萬"
                            />
                            <Button
                                danger
                                type="text"
                                size="small"
                                class="level-modal__rule-del"
                                @click="removeRule(idx)"
                            >
                                刪除
                            </Button>
                        </div>
                    </div>
                </div>

                <p
                    v-if="!isCreate && consultantCountHint > 0"
                    class="level-modal__warn"
                >
                    關閉啟用將影響 <strong>{{ consultantCountHint }}</strong> 位諮詢師
                </p>
            </div>
        </Modal>
    </div>
</template>

<style scoped>
.consultation-levels-page {
    max-width: 1200px;
}

.levels-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.levels-toolbar__title {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
}

.level-modal {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.level-modal__form {
    margin: 0;
}

.level-modal__grid {
    display: grid;
    grid-template-columns: 1.4fr 0.8fr 1fr;
    gap: 12px 14px;
    align-items: start;
}

.level-modal__grid--create {
    grid-template-columns: 0.9fr 1.2fr 0.7fr 1fr;
}

.level-modal__active {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 14px;
    padding: 12px 14px;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
}

.level-modal__active-label {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
}

.level-modal__active-hint {
    margin-top: 2px;
    font-size: 12px;
    color: #64748b;
}

.level-modal__rules-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
}

.level-modal__rules-title {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
}

.level-modal__rules-empty {
    margin: 0;
    padding: 14px 12px;
    border-radius: 10px;
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    color: #64748b;
    font-size: 13px;
    text-align: center;
}

.level-modal__rules-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.level-modal__rule {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) 110px minmax(0, 1fr) auto;
    gap: 8px;
    align-items: center;
    padding: 10px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background: #fff;
}

.level-modal__rule-metric,
.level-modal__rule-op,
.level-modal__rule-threshold {
    width: 100%;
    min-width: 0;
}

.level-modal__rule-del {
    padding-inline: 4px;
}

.level-modal__warn {
    margin: 0;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid #f7e6a8;
    background: #fffbe9;
    color: #6b5900;
    font-size: 13px;
}

@media (max-width: 640px) {
    .level-modal__grid,
    .level-modal__grid--create {
        grid-template-columns: 1fr 1fr;
    }

    .level-modal__rule {
        grid-template-columns: 1fr 1fr;
    }

    .level-modal__rule-threshold {
        grid-column: 1 / -1;
    }

    .level-modal__rule-del {
        grid-column: 1 / -1;
        justify-self: end;
    }
}
</style>
